import { useMemo, useState } from "react";
import { useFilters } from "@/context/FiltersContext";
import useIsMobile from "@/lib/useIsMobile";
import { fmtDate, money, daysUntilMountain, mountainTodayISO, addedMoneyForEvents } from "@/lib/ui";
import RodeoInfoDialog from "@/components/RodeoInfoDialog";
import { Loader2, Search, CalendarClock, ChevronRight } from "lucide-react";

// A single page listing every rodeo's entry OPEN and CLOSE dates, soonest close first.
export default function EntryDates() {
  const m = useIsMobile();
  const { rodeos, events } = useFilters();
  const [q, setQ] = useState("");
  const [onlyUpcoming, setOnlyUpcoming] = useState(true);
  const [info, setInfo] = useState(null);
  const today = mountainTodayISO();

  const rows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (rodeos || [])
      .filter((r) => r.entry_open || r.entry_close)
      .filter((r) => !qq || `${r.name} ${r.city || ""} ${r.state || r.province || ""}`.toLowerCase().includes(qq))
      .filter((r) => !onlyUpcoming || !r.entry_close || r.entry_close >= today)
      .sort((a, b) => (a.entry_close || "9999").localeCompare(b.entry_close || "9999"));
  }, [rodeos, q, onlyUpcoming, today]);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const inp = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400";

  return (
    <div className={page} data-testid="entry-dates-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <CalendarClock className="w-6 h-6 text-[#C8000F]" />
        <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>Entry Dates</h1>
      </div>

      <div className="p-4 sm:p-6 max-w-3xl space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a rodeo, town or state…" data-testid="entry-dates-search"
              className={`w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
          </div>
          <button onClick={() => setOnlyUpcoming((v) => !v)} data-testid="entry-dates-upcoming-toggle"
            className={`shrink-0 text-sm font-semibold px-4 py-2.5 rounded-lg border transition-colors ${onlyUpcoming ? "bg-[#C8000F] text-white border-[#C8000F]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>
            {onlyUpcoming ? "Upcoming only" : "Showing all"}
          </button>
        </div>

        {rodeos === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : (
          <>
            <div className="text-xs text-slate-400" data-testid="entry-dates-count">{rows.length} rodeos</div>
            <div className={`rounded-xl border overflow-hidden ${card}`}>
              <div className={`grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 text-[10px] font-bold uppercase tracking-wide ${m ? "text-slate-500 bg-black/20" : "text-slate-400 bg-[#FAFBFC]"}`}>
                <div>Rodeo</div><div className="text-right">Opens</div><div className="text-right">Closes</div>
              </div>
              {rows.length === 0 ? (
                <div className="px-3 py-10 text-center text-sm text-slate-400">No rodeos with entry dates match.</div>
              ) : rows.map((r) => {
                const dl = daysUntilMountain(r.entry_close);
                const soon = dl != null && dl >= 0 && dl <= 3;
                const closed = dl != null && dl < 0;
                return (
                  <button key={r.id} onClick={() => setInfo({ id: r.id, name: r.name })} data-testid={`entry-dates-row-${r.id}`}
                    className={`w-full text-left grid grid-cols-[1fr_auto_auto] gap-3 items-center px-3 py-2.5 border-t transition-colors ${m ? "border-white/5 active:bg-white/5" : "border-[#EEF0F2] hover:bg-[#FAFBFC]"}`}>
                    <div className="min-w-0">
                      <div className={`font-semibold text-sm truncate ${title}`}>{r.name}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {[r.city, r.state || r.province].filter(Boolean).join(", ")}
                        {(() => { const am = addedMoneyForEvents(r, events); return am.amount != null ? <span className="text-[#E68A2E]"> · {money(am.amount)}{am.partial ? "*" : ""}</span> : null; })()}
                      </div>
                    </div>
                    <div className="text-right text-[12px] text-slate-400 shrink-0">{r.entry_open ? fmtDate(r.entry_open) : "—"}</div>
                    <div className="text-right shrink-0">
                      <div className={`text-[12px] font-semibold ${closed ? "text-slate-400 line-through" : soon ? "text-[#C8000F]" : title}`}>
                        {r.entry_close ? fmtDate(r.entry_close) : "—"}
                      </div>
                      {dl != null && !closed && (
                        <div className={`text-[10px] font-bold ${soon ? "text-[#C8000F]" : "text-slate-400"}`}>
                          {dl === 0 ? "today" : dl === 1 ? "1 day" : `${dl} days`}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="hidden" />
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {info && <RodeoInfoDialog data={info} onClose={() => setInfo(null)} />}
    </div>
  );
}
